// src/views/words-list/CatalogWordButton.tsx
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from 'antd';
import { StarFilled } from '@ant-design/icons';
import { isUnsplashImage } from '../../utils/unsplashAttribution';
import { isCatalogTerm } from '../../utils/catalog';
import classes from './CatalogWordButton.module.less';

interface CatalogWordButtonProps {
  word: any;
  isInCatalog: boolean;
  isAdding: boolean;
  onAdd: (word: any) => void;
}

// Nazwa musi się zgadzać z `fill: url(#…)` w CatalogWordButton.module.less
const STAR_GRADIENT_ID = 'catalog-star-gradient';

// Do katalogu wchodzi tylko własny obrazek z `imageAssetId` — cudzy odrzuci backend, ale tego
// z przeglądarki nie widać, więc to wyjaśnia dopiero komunikat po kliknięciu.
const getBlockedReason = (word: any): string | null => {
  if (word.imageMeta?.source === 'unsplash' || isUnsplashImage(word.image)) {
    return 'words-list:catalog.word.unsplash';
  }

  if (!word.imageAssetId) {
    return 'words-list:catalog.word.noAsset';
  }

  if (!isCatalogTerm(word.term)) {
    return 'words-list:catalog.word.noTerm';
  }

  return null;
};

// Przycisk kuratora w nagłówku karty słowa: „★ Do katalogu" albo — gdy słowo już jest w katalogu —
// sama gwiazdka w gradiencie aplikacji
export const CatalogWordButton: FC<CatalogWordButtonProps> = ({
  word, isInCatalog, isAdding, onAdd,
}) => {
  const { t } = useTranslation();

  if (!word?.image) {
    return null;
  }

  if (isInCatalog) {
    const label = t('words-list:catalog.word.inCatalog');

    return (
      <Tooltip title={label}>
        <span className={classes.inCatalog} role="img" aria-label={label}>
          {/* Gradient dla gwiazdki — CSS-owy gradient nie wypełnia SVG. Ta sama nazwa na każdej karcie:
              przeglądarka bierze pierwszą definicję, a wszystkie są identyczne */}
          <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
            <defs>
              <linearGradient id={STAR_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00b0b0" />
                <stop offset="100%" stopColor="#002525" />
              </linearGradient>
            </defs>
          </svg>
          <StarFilled />
        </span>
      </Tooltip>
    );
  }

  const blockedReason = getBlockedReason(word);
  const button = (
    <Button
      size="small"
      className={classes.catalogButton}
      disabled={!!blockedReason}
      loading={isAdding}
      onClick={() => onAdd(word)}
    >
      {t('words-list:catalog.word.add')}
    </Button>
  );

  // Wyłączony przycisk nie odpala podpowiedzi sam — potrzebuje opakowania
  return blockedReason
    ? <Tooltip title={t(blockedReason)}><span>{button}</span></Tooltip>
    : <Tooltip title={t('words-list:catalog.word.addHint')}>{button}</Tooltip>;
};

export default CatalogWordButton;
